import React from 'react';
import { number, string } from 'prop-types';

import { Card, CardTitle } from '@thd-olt-component-react/core-ui';

import { CustomRenderedRecentlyViewed as RecentlyViewed } from './RecentlyViewed';
import { CustomRenderedTopSavingsForYou as TopSavingsForYou } from './TopSavingsForYou';
import { CustomRenderedBuyItAgain as BuyItAgain } from './BuyItAgain';
import { CustomRenderedTopCategoriesForYou as TopCategoriesForYou } from './TopCategoriesForYou';
import { CustomRenderedTrendingInYourNeighborhood as TrendingInYourNeighborhood } from './TrendingInYourNeighborhood';
/* eslint-disable max-len */
import { CustomRenderedTodaysRecommendationsForYou as TodaysRecommendationsForYou } from './TodaysRecommendationsForYou';
import { CustomRenderedSponsoredProducts as SponsoredProducts } from './SponsoredProducts';
import { CustomRenderedArticleGuides as ArticleGuides } from './ArticleGuides';
import { CustomRenderedPriceDrop as PriceDrop } from './PriceDrop';

const REQUEST_KEY = 'G4xP51k3Aw8tOEvGUKRHbmYW8cM9XrXX';

const RecommendationsComponentPropTypes = {
  id: string,
  customRenderId: string,
  renderWidths: {
    minWidthMedium: number,
    minWidthLarge: number,
  },
};

const RecommendationsComponentDefaultProps = {
  id: undefined,
  customRenderId: undefined,
  renderWidths: {},
};

/**
 * These are our global "_variables.scss" related to screen size breakpoints.
 * $screen-xs: 640px;
 * $screen-mini: 768px;
 * $screen-sm: 1024px;
 * $screen-md: 1280px;
 * $screen-lg: 1440px;
 *
 * In the object below the key is the size it is less than and the value is the number of `slidesPer`
 * for Recs Carousels (they have their own way of handling this; `slidesPer` is not used in other carousels)
 */
const SLIDES_PER_KEY = {
  minWidthMedium: {
    4: 2,
    6: 3,
    8: 4,
    12: 5,
  },
  minWidthLarge: {
    4: 2,
    6: 4,
    8: 5,
    12: 7,
  },
};

const SLIDES_PER_KEY_ARTICLE_GUIDES = {
  minWidthMedium: {
    4: 1,
    6: 1,
    8: 2,
    12: 2,
  },
  minWidthLarge: {
    4: 1,
    6: 2,
    8: 2,
    12: 3,
  },
};

function getSlidesPer({ renderWidths, slidesPerKey, windowWidthMeasure }) {
  return windowWidthMeasure === 'maxWidthMedium'
    ? null
    : slidesPerKey[windowWidthMeasure][+renderWidths[windowWidthMeasure]];
}

export function CustomRenderedRecommendations({
  SCROLL_BUFFER,
  HOMEPAGE_DELAY_STANDARD,
  PLACEHOLDER,
  store,
  customerType,
  windowWidthMeasure,
  guestUserId,
  mcvisID,
  userAuthStatus,
}) {
  function RecommendationsComponent({ customRenderId, renderWidths, id }) {
    const brandTitleMaxLine = 2;

    const slidesPer = getSlidesPer({
      renderWidths,
      slidesPerKey: SLIDES_PER_KEY,
      windowWidthMeasure,
    });

    switch (customRenderId) {
    case 'Recently Viewed': {
      return RecentlyViewed({
        SCROLL_BUFFER,
        HOMEPAGE_DELAY_STANDARD,
        PLACEHOLDER,

        id,
        slidesPer,
        brandTitleMaxLine,
      });
    }

    case 'Top Savings for You': {
      return TopSavingsForYou({
        SCROLL_BUFFER,
        HOMEPAGE_DELAY_STANDARD,
        PLACEHOLDER,

        store,
        customerType,
        id
      });
    }

    case 'Buy It Again': {
      return BuyItAgain({
        SCROLL_BUFFER,
        HOMEPAGE_DELAY_STANDARD,
        PLACEHOLDER,
        REQUEST_KEY,

        id,
        brandTitleMaxLine,
        slidesPer,
      });
    }

    case 'Top Categories for You': {
      return TopCategoriesForYou({
        SCROLL_BUFFER,
        HOMEPAGE_DELAY_STANDARD,
        PLACEHOLDER,
        REQUEST_KEY,

        mcvisID,
        slidesPer,
        customerType,
      });
    }

    case 'Trending In Your Neighborhood': {
      return TrendingInYourNeighborhood({
        SCROLL_BUFFER,
        HOMEPAGE_DELAY_STANDARD,
        PLACEHOLDER,

        store,
        slidesPer,
      });
    }

    case 'Todays Recommendations for You': {
      return TodaysRecommendationsForYou({
        SCROLL_BUFFER,
        HOMEPAGE_DELAY_STANDARD,
        PLACEHOLDER,

        id,
        brandTitleMaxLine,
        store,
      });
    }

    case 'Sponsored Products': {
      return SponsoredProducts({
        SCROLL_BUFFER,
        HOMEPAGE_DELAY_STANDARD,
        PLACEHOLDER,

        userAuthStatus,
        customRenderId,
        slidesPer,
      });
    }

    case 'Article Guides': {
      /* eslint-disable no-shadow */
      const slidesPer = getSlidesPer({
        renderWidths,
        slidesPerKey: SLIDES_PER_KEY_ARTICLE_GUIDES,
        windowWidthMeasure,
      });

      return ArticleGuides({
        SCROLL_BUFFER,
        HOMEPAGE_DELAY_STANDARD,
        PLACEHOLDER,

        REQUEST_KEY,
        guestUserId,
        slidesPer,
        customRenderId,
      });
    }

    case 'Price Drop': {
      return PriceDrop({
        SCROLL_BUFFER,
        HOMEPAGE_DELAY_STANDARD,
        PLACEHOLDER,
        REQUEST_KEY,

        brandTitleMaxLine,
        slidesPer,
      });
    }

    default: {
      return (
        <Card>
          <CardTitle title={customRenderId} />
        </Card>
      );
    }
    }
  }

  RecommendationsComponent.propTypes = RecommendationsComponentPropTypes;
  RecommendationsComponent.defaultProps = RecommendationsComponentDefaultProps;

  return RecommendationsComponent;
}
